/**
 * API Service Layer
 * Centralized API client and endpoint definitions
 */

import { http } from '../network/http';
import type { AxiosResponse } from 'axios';

// Re-export http instance as api for convenience
export const api = http;

// ============================================================================
// AUTH API
// ============================================================================

export const authApi = {
  login: (data: { email: string; password: string }) =>
    http.post('/auth/login', data),

  register: (data: { email: string; password: string; firstName?: string; lastName?: string }) =>
    http.post('/auth/register', data),

  googleSignIn: (data: { credential: string }) =>
    http.post('/auth/google-signin', data),

  logout: () =>
    http.post('/auth/logout'),

  logoutAll: () =>
    http.post('/auth/logout-all'),

  refresh: () =>
    http.post('/auth/refresh'), // Refresh token is now sent via HttpOnly cookie

  forgotPassword: (data: { email: string }) =>
    http.post('/auth/forgot-password', data),

  resetPassword: (data: { token: string; newPassword: string }) =>
    http.post('/auth/reset-password', data),

  getMe: () =>
    http.get('/auth/me'),
};

// ============================================================================
// USERS API
// ============================================================================

export const usersApi = {
  getUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    isActive?: boolean;
    organizationId?: string;
  }) =>
    http.get('/users', { params }),

  getUser: (id: string) =>
    http.get(`/users/${id}`),

  createUser: (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    organizationId?: string;
  }) =>
    http.post('/users', data),

  updateUser: (id: string, data: {
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    isActive?: boolean;
    organizationId?: string;
  }) =>
    http.patch(`/users/${id}`, data),

  deleteUser: (id: string) =>
    http.delete(`/users/${id}`),

  changeUserPassword: (id: string, data: { newPassword: string }) =>
    http.patch(`/users/${id}/password`, data),

  getMyProfile: () =>
    http.get('/users/me'),

  updateMyProfile: (data: {
    firstName?: string;
    lastName?: string;
  }) =>
    http.patch('/users/me', data),

  changeMyPassword: (data: { currentPassword: string; newPassword: string }) =>
    http.patch('/users/me/password', data),
};

// ============================================================================
// RESOURCES API (Generic CRUD - customize for your domain)
// ============================================================================

export const resourcesApi = {
  getResources: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    organizationId?: string;
  }) =>
    http.get('/resources', { params }),

  getResource: (id: string) =>
    http.get(`/resources/${id}`),

  createResource: (data: {
    name: string;
    description?: string;
    metadata?: Record<string, any>;
    organizationId?: string;
  }) =>
    http.post('/resources', data),

  updateResource: (id: string, data: {
    name?: string;
    description?: string;
    metadata?: Record<string, any>;
    isActive?: boolean;
    organizationId?: string;
  }) =>
    http.patch(`/resources/${id}`, data),

  deleteResource: (id: string) =>
    http.delete(`/resources/${id}`),
};

// ============================================================================
// ANALYTICS API (Optional - customize for your needs)
// ============================================================================

export const analyticsApi = {
  getDashboardAnalytics: (params?: {
    timeFilter?: string;
    organizationId?: string;
  }) =>
    http.get('/analytics/dashboard', { params }),

  getResourceStatusAnalytics: () =>
    http.get('/analytics/resource-status'),

  getCharts: () =>
    http.get('/analytics/charts'),

  getChart: (id: string) =>
    http.get(`/analytics/charts/${id}`),

  saveChart: (data: any) =>
    http.post('/analytics/charts', data),

  deleteChart: (id: string) =>
    http.delete(`/analytics/charts/${id}`),
};

// ============================================================================
// HEALTH API
// ============================================================================

export const healthApi = {
  check: () =>
    http.get('/health'),

  checkDatabase: () =>
    http.get('/health/db'),
};

// ============================================================================
// Type-safe Response Helpers
// ============================================================================

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: boolean;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Extract data from Axios response
 */
export function extractData<T>(response: AxiosResponse<ApiResponse<T>>): T {
  return response.data.data as T;
}

/**
 * Check if response is successful
 */
export function isSuccess(response: AxiosResponse<ApiResponse>): boolean {
  return response.data.success === true;
}

// Export all APIs as a single object for convenience
export default {
  auth: authApi,
  users: usersApi,
  resources: resourcesApi,
  analytics: analyticsApi,
  health: healthApi,
};
